import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, LogOut, Eye, Pencil } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { getCurrentUser } from '../../utils/auth';
import { getImpersonation, clearImpersonation } from '../../utils/impersonation';
import './impersonation.scss';

/**
 * ImpersonationBanner — always-visible reminder that this tab is a platform
 * support session, not the operator's own account.
 *
 * Mounted once in Layout, which every role tree (admin, student, instructor)
 * renders under, so a support session is labelled no matter who was
 * impersonated. The label is not decoration: without it an operator can lose
 * track of whose data is on screen, and everything they do here is attributed
 * to the customer's user.
 */

/** "42m left" / "1h 05m left"; null once the hard expiry has passed. */
function formatRemaining(expiresAt) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m left`;
  return `${Math.max(totalMinutes, 1)}m left`;
}

const ImpersonationBanner = () => {
  const [imp, setImp] = useState(() => getImpersonation());
  const [remaining, setRemaining] = useState(null);
  const [exiting, setExiting] = useState(false);
  const [blocked, setBlocked] = useState('');

  const exit = useCallback(async () => {
    setExiting(true);
    try {
      // Best-effort: the session must be released locally even if the server
      // call fails, or the operator is trapped in the customer's account.
      await apiClient.post('/auth/impersonate/stop');
    } catch { /* the session may already be revoked or expired */ }

    const { restored, returnUrl } = clearImpersonation();
    setImp(null);

    if (restored) {
      // Same origin as the console (local dev): the operator's own session is
      // back in place, so just reload into it.
      window.location.href = '/';
      return;
    }
    // Always back to the console — never leave the operator sitting on a login
    // page on the customer's domain, where the only way forward is to type
    // credentials they do not have.
    window.location.href = returnUrl || '/login';
  }, []);

  // Countdown, hard-expiry auto-exit, and a liveness check.
  //
  // The poll matters because a support session can end from OUTSIDE this tab —
  // superseded by a newer one, or revoked from the operator console. Without it
  // an idle tab keeps showing a confident banner over a dead session until the
  // operator clicks something and gets a surprise 401. (A revoked session's
  // poll 401s and apiClient tears down cleanly; this GET also catches the
  // narrower case where the session record was ended but its token was not.)
  useEffect(() => {
    if (!imp?.expiresAt) return undefined;
    let stopped = false;

    const tick = async () => {
      if (stopped) return;
      const left = formatRemaining(imp.expiresAt);
      setRemaining(left);
      if (!left) { exit(); return; }
      try {
        const { data } = await apiClient.get('/auth/impersonate/context');
        if (!stopped && data && data.impersonating === false) exit();
      } catch { /* 401 is handled by apiClient; anything else is transient */ }
    };

    tick();
    const id = setInterval(tick, 30000);
    return () => { stopped = true; clearInterval(id); };
  }, [imp, exit]);

  // A write was refused because this session is read-only (apiClient raises the
  // event). Say so on the banner, where the reason already lives.
  useEffect(() => {
    let timer;
    const onBlocked = (e) => {
      setBlocked(e.detail?.message || 'Blocked — this support session is read-only.');
      clearTimeout(timer);
      timer = setTimeout(() => setBlocked(''), 6000);
    };
    window.addEventListener('vc-impersonation-readonly', onBlocked);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('vc-impersonation-readonly', onBlocked);
    };
  }, []);

  if (!imp) return null;

  const user = getCurrentUser();
  const who = user?.name || imp.targetName || 'this user';

  return (
    <div className={`imp-banner${imp.readOnly ? '' : ' imp-banner--write'}`} role="status">
      <ShieldAlert size={16} className="imp-banner__icon" />
      <div className="imp-banner__text">
        {blocked ? (
          <span className="imp-banner__blocked">{blocked}</span>
        ) : (
          <>
            <strong>Support session</strong>
            <span className="imp-banner__sep">·</span>
            viewing as <strong>{who}</strong>
            {imp.instituteName && <> at <strong>{imp.instituteName}</strong></>}
          </>
        )}
      </div>

      <span className={`imp-banner__mode${imp.readOnly ? '' : ' imp-banner__mode--write'}`}>
        {imp.readOnly ? <Eye size={12} /> : <Pencil size={12} />}
        {imp.readOnly ? 'Read only' : 'Changes enabled'}
      </span>

      {remaining && <span className="imp-banner__timer">{remaining}</span>}

      <button
        type="button"
        className="imp-banner__exit"
        onClick={() => exit(false)}
        disabled={exiting}
      >
        <LogOut size={13} />
        {exiting ? 'Exiting…' : 'Exit'}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
